<template>
  <AllocationCardLayout
    icon="handshake"
    :color="PALETTE.semanticOverrides.portfolioType.PPA"
    :title="$t('production_strategy.ppa')"
    :amount="allocation.amount"
    :unit="$t('common.mwh')"
    :subtitle="$t('production_strategy.allocated')"
    :ariaLabel="$t('production_strategy.ppa_allocation_card')"
    :expandable="true"
    :expandButtonText="$t('production_strategy.ppa_agreements')"
    :hasExpandableContent="hasDisplayableContent"
  >
    <template #badge>
      <span
        class="badge text-white px-3 py-2"
        :style="{
          backgroundColor: PALETTE.semanticOverrides.portfolioType.PPA,
        }"
      >
        {{ allocation.agreementCount }}
        {{ $t("production_strategy.agreements") }}
      </span>
    </template>
    <template #action>
      <button
        v-if="canEdit"
        class="btn btn-outline-primary btn-sm"
        @click="openPPASettings"
        :disabled="false"
      >
        <FontAwesomeIcon :icon="['fas', 'plus']" class="me-1" />
        {{ $t("common.add") }}
      </button>
    </template>
    <template #content>
      <div class="p-3">
        <div v-if="hasDisplayableContent" class="d-flex flex-column gap-2">
          <PPAOverview
            v-for="portfolio in ppaPortfoliosWithInstructions"
            :key="portfolio.id"
            :portfolio="portfolio"
            :companies="companies"
            :registryDevices="registryDevices"
            :individualAllocation="
              getIndividualAllocationForPortfolio(portfolio.id || 0)
            "
          >
            <template #edit-button>
              <EditButton
                v-if="canEdit"
                @click="openPPAEditSettings(portfolio)"
              />
            </template>
          </PPAOverview>
        </div>
        <p v-else class="mb-0 text-muted fs-8">
          {{ $t("ppa_strategy.no_ppa_instructions") }}
        </p>
      </div>
    </template>
  </AllocationCardLayout>

  <!-- PPA Settings Modal -->
  <ModalComponent
    ref="ppaModalRef"
    class="ppa-modal"
    :submit-button-text="$t('common.save')"
    :hide-submit-button="true"
    :no-footer="true"
    size="xl"
  >
    <template #title>
      <span class="ppa-modal__crumb">{{ modalCrumb }}</span>
      <span class="ppa-modal__heading">{{ modalHeading }}</span>
    </template>
    <template #body>
      <PPAInstructionsEditor
        v-if="editingPortfolio && currentCompany?.id"
        ref="ppaEditorRef"
        :usesCnmcIssuance="
          hasCnmcConnection(currentCompany?.registry_connections)
        "
        v-model="editingPortfolio"
        :registryDevices="registryDevices"
        :existingPortfolios="ppaPortfoliosWithInstructions"
        :individualAllocation="
          getIndividualAllocationForPortfolio(editingPortfolio.id || 0)
        "
        :mode="editorMode"
        :showAllModeFooter="editorMode === 'all'"
        @save="savePPASettings"
        @cancel="closeModal"
        @step-change="handleStepChange"
        @show-all="handleShowAll"
        @error:ai-merge="globalErrorHandler"
      />

      <div v-else class="text-center py-4">
        <p>{{ $t("common.loading") }}...</p>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import { hasCnmcConnection } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcPresentation";
import EditButton from "@/components/common/EditButton.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { PALETTE } from "@/composables/chartPalette";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { PPAIndividualAllocation } from "@/dashboard/production_strategy/overview/useProductionAllocationData";
import type {
  Company,
  PortfolioWithInstructions,
  RegistryDevice,
} from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useQueryManager } from "@/composables/useQueryManager";

import { filterPortfoliosWithInstructions } from "@/composables/usePortfolioUtils";
import { useProductionStrategyStore } from "@/dashboard/production_strategy/useProductionStrategyStore";
import AllocationCardLayout from "../AllocationCardLayout.vue";
import { useCompanyMode } from "../useCompanyMode";
import PPAInstructionsEditor from "./PPAInstructionsEditor.vue";
import PPAOverview from "./PPAOverview.vue";

const props = defineProps<{
  allocation: {
    amount: number;
    percentage: number;
    agreementCount?: number;
  };
  companies: Company[];
  portfolios?: PortfolioWithInstructions[];
  registryDevices: RegistryDevice[];
  individualAllocations?: PPAIndividualAllocation[];
}>();

const emit = defineEmits<{
  success: [message: string];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const productionStore = useProductionStrategyStore();
const { updateQuery, getQueryValue, parsers } = useQueryManager();

const editPpaId = getQueryValue("edit-ppa-id", parsers.number);

// Multi-company mode detection
const { currentCompany, canEdit } = useCompanyMode(
  computed(() => props.companies)
);

const ppaModalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("ppaModalRef");
const ppaEditorRef =
  useTemplateRef<InstanceType<typeof PPAInstructionsEditor>>("ppaEditorRef");
const editingPortfolio = ref<PortfolioWithInstructions | null>(null);
const isCreatingNew = ref(false);
const currentWizardStep = ref(0);
// Editor mode defaults from isCreatingNew but can be flipped to "all" by the
// wizard's "Show all as one form" escape hatch without losing the create flow.
const editorModeOverride = ref<"wizard" | "all" | null>(null);

const editorMode = computed<"wizard" | "all">(() => {
  if (editorModeOverride.value) return editorModeOverride.value;
  return isCreatingNew.value ? "wizard" : "all";
});

const wizardStepTitles = computed(() => [
  t("production_strategy.wizard_step_action"),
  t("production_strategy.wizard_step_allocation"),
  t("production_strategy.wizard_step_execution"),
]);

const modalHeading = computed(() =>
  isCreatingNew.value
    ? t("production_strategy.new_ppa_portfolio")
    : t("production_strategy.edit_ppa_portfolio")
);

const modalCrumb = computed(() => {
  const base = t("production_strategy.ppa_modal_crumb");
  if (editorMode.value !== "wizard") return base;
  const stepTitle = wizardStepTitles.value[currentWizardStep.value];
  return stepTitle ? `${base} · ${stepTitle}` : base;
});

const handleStepChange = (stepIndex: number) => {
  currentWizardStep.value = stepIndex;
};

const handleShowAll = () => {
  editorModeOverride.value = "all";
};

// Filter PPA portfolios with instructions
const ppaPortfoliosWithInstructions = computed(() => {
  if (!props.portfolios) return [];
  const ppaPortfolios = props.portfolios.filter(
    (p) => p.portfolio_type === "PPA"
  );

  return filterPortfoliosWithInstructions(ppaPortfolios);
});

// Check if we have displayable content (back to original approach)
const hasDisplayableContent = computed(() => {
  return ppaPortfoliosWithInstructions.value.length > 0;
});

// Helper function to get individual allocation data for a portfolio
const getIndividualAllocationForPortfolio = (portfolioId: number) => {
  if (!props.individualAllocations) return null;
  return (
    props.individualAllocations.find(
      (allocation) => allocation.portfolio_id === portfolioId
    ) || null
  );
};

// Modal handlers
const openPPASettings = () => {
  if (!currentCompany.value?.id) return;
  isCreatingNew.value = true;
  editorModeOverride.value = null;
  currentWizardStep.value = 0;
  editingPortfolio.value = productionStore.createNewPPAPortfolio(
    currentCompany.value.id
  );
  if (ppaModalRef.value) {
    ppaModalRef.value.showModal = true;
  }
};

const openPPAEditSettings = (portfolio: PortfolioWithInstructions) => {
  isCreatingNew.value = false;
  editorModeOverride.value = null;
  currentWizardStep.value = 0;
  editingPortfolio.value = { ...portfolio };
  if (ppaModalRef.value) {
    ppaModalRef.value.showModal = true;
  }
};

// Deep-link: Portfolios tab sets edit-ppa-id query param to open the editor for a specific PPA.
// Only clear the query param AFTER we successfully find + open the portfolio — otherwise the
// watcher may fire before the correct company's portfolios have loaded (the store can hold
// stale portfolios from a previously viewed company), and clearing prematurely would drop
// the deep-link before the real data arrives.
const handleDeepLink = async () => {
  try {
    const ppaId = editPpaId.value;
    if (!ppaId) return;

    const portfolio = ppaPortfoliosWithInstructions.value.find(
      (p) => p.id === ppaId
    );
    if (!portfolio) return;

    await updateQuery({ "edit-ppa-id": undefined });
    await nextTick();
    openPPAEditSettings(portfolio);
  } catch (error) {
    globalErrorHandler(error);
  }
};

onMounted(() => {
  void handleDeepLink();
});

watch([editPpaId, ppaPortfoliosWithInstructions], () => {
  void handleDeepLink();
});

const closeModal = () => {
  if (ppaModalRef.value) {
    ppaModalRef.value.showModal = false;
  }
};

const savePPASettings = async () => {
  try {
    if (!editingPortfolio.value || !currentCompany.value?.id) {
      throw new Error("Missing portfolio or company data");
    }

    // Editor validates itself before emitting @save; if we got here it's OK
    // to persist. Still guard defensively against future changes.
    if (
      ppaEditorRef.value &&
      typeof (ppaEditorRef.value as any).touchAndValidate === "function"
    ) {
      const ok = (ppaEditorRef.value as any).touchAndValidate();
      if (!ok) return;
    }

    const success = await productionStore.savePortfolio(
      currentCompany.value.id,
      editingPortfolio.value
    );

    if (success) {
      globalSuccessHandler(
        t("portfolio_instructions.update_success_title"),
        t("portfolio_instructions.update_success")
      );

      closeModal();

      // Emit success event to parent for data refresh
      emit("success", t("portfolio_instructions.update_success"));
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};
</script>

<style scoped lang="scss">
// Narrow the PPA modal from the default xl (1140px) to the ~960px width in
// the design canvas — the wizard layout was sized for that proportion.
.ppa-modal > :deep(.modal-dialog) {
  --phoenix-modal-width: 960px;
  max-width: 960px;
}

.ppa-modal > :deep(.modal-dialog) .modal-header {
  flex-direction: row;
  align-items: flex-start;
  padding: 22px 28px 18px;
  border-bottom: 1px solid var(--phoenix-border-color-translucent);
  gap: 20px;
}

.ppa-modal > :deep(.modal-dialog) .modal-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  line-height: 1.2;
}

.ppa-modal__crumb {
  font-size: 12px;
  font-weight: 600;
  color: var(--phoenix-gray-500);
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.ppa-modal__heading {
  font-size: 20px;
  font-weight: 800;
  color: var(--phoenix-gray-1000);
  letter-spacing: -0.01em;
}
</style>
